import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PhoneIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { Spinner } from '../../ui/Spinner';
import { NoAccess } from '../ui/RequirePermission';
import { LoadFailed } from '../ui/LoadState';
import { btnSecondary } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { ApiError, isAbortError } from '../../core/api/http';
import { fetchStaffCustomer } from '../adminApi';
import { dayMedium } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { OWNER_STAFF_ID, staffList, staffName } from '../../core/rules/salonDays';
import { HandoffNoteForm } from './HandoffNoteForm';
import type { StaffCustomerCard as Card } from '../../core/types/adminRequests';
type State = {
  status: 'loading';
} | {
  status: 'ok';
  card: Card;
} | {
  status: 'forbidden';
} | {
  status: 'error';
  message: string;
};
const back = {
  to: '/be/agenda',
  label: 'Agenda'
};

/**
 * De klantkaart voor een collega: alleen wat ze voor de behandeling nodig heeft. Naam, telefoon,
 * huidplan en eerdere behandelingen met notities. Geen bestellingen, tegoed, abonnement, foto's of
 * de interne notitie van Sarah (docs/KASSA-ABONNEMENTEN.md §4). De server geeft 403 als de klant
 * geen afspraak bij haar heeft.
 */
export function StaffCustomerCard() {
  const {
    id = ''
  } = useParams();
  const {
    site,
    index
  } = useAdmin();
  const team = staffList(site.staff);
  const [state, setState] = useState<State>({
    status: 'loading'
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setState({
      status: 'loading'
    });
    fetchStaffCustomer(id, {
      signal: controller.signal
    }).then(card => setState({
      status: 'ok',
      card
    })).catch((err: unknown) => {
      if (isAbortError(err)) return;
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) setState({
        status: 'forbidden'
      });else setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Laden lukte niet.'
      });
    });
    return () => controller.abort();
  }, [id, attempt]);
  if (state.status === 'forbidden') return <NoAccess data-id="element-1506" data-mp="8el0y2o5d00g" />;
  if (state.status === 'loading') return <div className="flex items-center gap-2 py-16 text-[14px] text-muted" role="status" data-id="element-1507" data-mp="8da28h9k6ig7">
        <Spinner data-id="element-1508" data-mp="dc3d01xgnvco" /> Klantkaart laden…
      </div>;
  if (state.status === 'error') return <LoadFailed title="De klantkaart kon niet laden" text={state.message} onRetry={() => setAttempt(n => n + 1)} data-id="element-1509" data-mp="3btqm5jn7v8hh" />;
  const {
    card
  } = state;
  const treatmentName = (slug: string) => index.treatmentBySlug(slug)?.name ?? slug;
  return <div data-id="element-1510" data-mp="3qkzg24y5jg0f">
      <PageHeader title={card.name} description="Wat je nodig hebt voor de behandeling" back={back} data-id="element-1511" data-mp="3qlrb9enu7bq6" />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-1512" data-mp="2t4aemcd46frk">
        <section aria-labelledby="eerder" className="min-w-0" data-id="element-1513" data-mp="2y3vh2wrht691">
          <h2 id="eerder" className="text-[15px] font-medium text-ink" data-id="element-1514" data-mp="2y2by2yegch3k">
            Eerdere behandelingen
          </h2>
          {card.treatments.length === 0 ? <p className="mt-2 text-[14px] text-muted" data-id="element-1515" data-mp="2ln48i4uru6p6">Dit wordt haar eerste behandeling.</p> : <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-1516" data-mp="2gl520m3qito0">
              {card.treatments.map((t, i) => <li key={`${t.date}-${i}`} className="py-3" data-id="element-1517" data-mp="2bpzj7w5mpv45" data-mpi={i}>
                  <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-[14px]" data-id="element-1518" data-mp="27mth90pjuq3h">
                    <span className="text-ink" data-id="element-1519" data-mp="22ujj4xea6jq4">{treatmentName(t.treatmentSlug)}</span>
                    <span className="text-[13px] text-muted" data-id="element-1520" data-mp="1xx9x7rq3oq71">
                      {dayMedium(t.date)}
                      {team.length > 1 ? ` · behandeld door ${staffName(team, t.staffId)}` : ''}
                    </span>
                  </p>
                  {t.note && <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-muted" data-id="element-1521" data-mp="1jwfkwxzxm533">{t.note}</p>}
                </li>)}
            </ol>}
        </section>

        <aside className="space-y-6" data-id="element-1522" data-mp="28pfvanz8ou30">
          {card.attention && card.attention.length > 0 && <section aria-labelledby="let-op" className="rounded-md bg-blush/60 px-3 py-2.5" data-id="element-1523" data-mp="2do0v4makxbqx">
              <h2 id="let-op" className="text-[13px] font-medium text-ink" data-id="element-1524" data-mp="1zsysc81142ic">
                Let op
              </h2>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[13px] text-ink" data-id="element-1525" data-mp="1pwvh8co78tj9">
                {card.attention.map(x => <li key={x} data-id="element-1526" data-mp="1uwq5e8glb50m">{x}</li>)}
              </ul>
            </section>}
          {card.phone && <a href={`tel:${card.phone.replace(/\s/g, '')}`} className={btnSecondary} data-id="element-1527" data-mp="1gy0amzh8thff">
              <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1528" data-mp="1kxsmbl1xo3p0" />
              {card.phone}
            </a>}
          <section aria-labelledby="huidplan" data-id="element-1529" data-mp="3fbjv6icfujje">
            <h2 id="huidplan" className="text-[15px] font-medium text-ink" data-id="element-1530" data-mp="3fa8ly3084jkw">
              Huidplan
            </h2>
            {card.plan ? <div className="mt-2 space-y-1.5 text-[14px]" data-id="element-1531" data-mp="zmlbm35yjdec">
                {card.plan.focus && <p className="text-ink" data-id="element-1532" data-mp="zkicmtmtu9xh">{card.plan.focus}</p>}
                {card.plan.note && <p className="whitespace-pre-line text-[13px] leading-relaxed text-muted" data-id="element-1533" data-mp="1dfkr455qp7lw">{card.plan.note}</p>}
                <p className="text-[13px] text-muted" data-id="element-1534" data-mp="18l6p3jwko289">{plural(card.plan.items.length, 'product', 'producten')} voor thuis</p>
              </div> : <p className="mt-2 text-[14px] text-muted" data-id="element-1535" data-mp="1ic1dattdpbwc">Nog geen huidplan.</p>}
          </section>
          <HandoffNoteForm customerId={card.id} to={staffName(team, OWNER_STAFF_ID)} data-id="element-1536" data-mp="1wbkltnq3qp96" />
        </aside>
      </div>
    </div>;
}